import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import React, { FC, useCallback, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { getListProjectsQueryKey, listProjects } from "~/api/folders/folders";
import { EmptySpace } from "~/components/EmptySpace";
import { useOrganizationParams } from "~/helpers";
import { ArchivedCasesListHeader } from "./ArchivedCasesListHeader";
import { ArchivedCasesListItem } from "./ArchivedCasesListItem";

const ITEMS_PER_PAGE = 20;

export const ArchivedCasesList: FC = () => {
  const { formatMessage } = useIntl();
  const { organizationId } = useOrganizationParams();
  const observerTarget = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: getListProjectsQueryKey(organizationId, { archived: true }),
      queryFn: async ({ pageParam = 1 }) => {
        const response = await listProjects(organizationId, {
          archived: true,
          page: pageParam as number,
          limit: ITEMS_PER_PAGE,
        });
        return response;
      },
      getNextPageParam: (lastPage) => {
        if (!lastPage?.pagination) return undefined;
        if (lastPage.pagination.currentPage < lastPage.pagination.totalPages) {
          return lastPage.pagination.currentPage + 1;
        }
        return undefined;
      },
      initialPageParam: 1,
    });

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const option = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    };

    const observer = new IntersectionObserver(handleObserver, option);
    observer.observe(element);

    return () => observer.disconnect();
  }, [handleObserver]);

  if (isLoading) {
    return null;
  }

  if (data?.pages.flatMap((page) => page?.projects || []).length === 0) {
    return (
      <EmptySpace
        title={formatMessage({
          id: "archivedCases.empty",
          defaultMessage: "No cases archived yet",
        })}
      />
    );
  }

  return (
    <div>
      <ArchivedCasesListHeader />
      {data?.pages.map((page, i) => (
        <React.Fragment key={i}>
          {(page?.projects || []).map((item) => (
            <ArchivedCasesListItem key={item.id} folder={item} />
          ))}
        </React.Fragment>
      ))}
      <div ref={observerTarget} className="h-10" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
        </div>
      )}
    </div>
  );
};
